import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Dialog, Flex, Text } from "@radix-ui/themes";
import mermaid from "mermaid";
import styles from "./PolicyFlowDialog.module.scss";

interface PolicyFlowDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mermaidCode: string;
  title: string;
}

export function PolicyFlowDialog({
  open,
  onOpenChange,
  mermaidCode,
  title,
}: PolicyFlowDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [renderedSvg, setRenderedSvg] = useState<string>("");

  const handleClose = useCallback(() => {
    onOpenChange(false);
    setError(null);
    setRenderedSvg("");
  }, [onOpenChange]);

  const sanitizeMermaidCode = useMemo(() => {
    if (!mermaidCode) return "";

    const collapseSpacesNotNewlines = (s: string) =>
      s.replace(/[ \t\f\v]+/g, " ");

    const sanitizeLabels = (code: string) =>
      code.replace(/\[([\s\S]*?)\]/g, (_, inner) => {
        const safeInner = inner
          .replace(/[\r\n]+/g, " ")
          .replace(/[—–]/g, "-")
          .replace(/[’‘]/g, "'")
          .replace(/[<>]/g, "")
          .replace(/&/g, "and")
          .replace(/\/\//g, "/")
          .slice(0, 250);
        return `[${safeInner}]`;
      });

    const sanitized = collapseSpacesNotNewlines(
      sanitizeLabels(mermaidCode)
    ).trim();
    return sanitized;
  }, [mermaidCode]);

  const renderMermaid = useCallback(async () => {
    if (!mermaidCode) return;

    setIsLoading(true);
    setError(null);
    setRenderedSvg("");

    try {
      const sanitizedCode = sanitizeMermaidCode;
      if (!sanitizedCode) {
        setError("No diagram code provided");
        return;
      }

      mermaid.initialize({
        startOnLoad: false,
        theme: "default",
        securityLevel: "loose",
        fontFamily: "inherit",
        flowchart: {
          useMaxWidth: true,
          htmlLabels: true,
        },
        sequence: {
          useMaxWidth: true,
        },
      });

      const id = `mermaid-${Date.now()}`;
      const { svg } = await mermaid.render(id, sanitizedCode);

      setRenderedSvg(svg);
    } catch (err) {
      console.error("Mermaid rendering error:", err);
      setError(
        `Failed to render diagram: ${
          err instanceof Error ? err.message : "Unknown error"
        }`
      );
    } finally {
      setIsLoading(false);
    }
  }, [mermaidCode]);

  useEffect(() => {
    if (open && mermaidCode) {
      renderMermaid();
    }
  }, [open, mermaidCode, renderMermaid]);

  const dialogContent = useMemo(() => {
    if (isLoading) {
      return (
        <Flex direction="column" align="center" gap="3" py="6">
          <Text size="2" color="gray">
            Rendering diagram...
          </Text>
        </Flex>
      );
    }

    if (error) {
      return (
        <Flex direction="column" align="center" gap="3" py="6">
          <Text size="2" color="red">
            {error}
          </Text>
        </Flex>
      );
    }

    if (renderedSvg) {
      return (
        <div
          className={styles.mermaidContainer}
          dangerouslySetInnerHTML={{ __html: renderedSvg }}
        />
      );
    }

    return (
      <Flex direction="column" align="center" gap="3" py="6">
        <Text size="2" color="gray">
          No diagram to display
        </Text>
      </Flex>
    );
  }, [isLoading, error, renderedSvg]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="80vw" className={styles.dialogContent}>
        <Dialog.Title>{title}</Dialog.Title>

        <div className={styles.diagramContainer}>{dialogContent}</div>

        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray" onClick={handleClose}>
              Close
            </Button>
          </Dialog.Close>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
